import { baseApi } from 'shared/api/baseApi'
import {
    INDIVIDUAL_USER,
    ORGANIZATION_TAG,
    ROLES_TAG,
    UNIT_ROLES_AND_PERMISSIONS_TAG,
    USERS_TAG,
} from 'shared/api/tags'

import type { RequestAddPermissionBody, UserRoles } from '..'
import type { RequestEditPermissionBody, UserRole } from '../model/types'

/**
 * Roles API that provides endpoints for managing user roles within the application.
 * @module rolesApi
 */
export const rolesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        /**
         * Fetches the roles of a specific user by their ID.
         * @param {string} id - The ID of the user.
         * @returns {Array<UserRoles>} - The roles associated with the user.
         */
        getUserRoles: build.query<UserRoles[], string>({
            query: (id) => ({
                url: `users/${id}/user-roles`,
                method: 'GET',
            }),
            providesTags: [ROLES_TAG],
        }),

        /**
         * Fetches all roles.
         * @returns {UserRoles} - All available roles.
         */
        getRoles: build.query<UserRole[], Params>({
            query: (params) => ({
                url: `roles`,
                method: 'GET',
                params,
            }),
            transformResponse: (response: UserRole[]) => {
                return response
            },
        }),

        /**
         * Assigns a role to a user.
         * @param {RequestAddPermissionBody} body - The body of the request containing user ID, role ID, organization ID, sport IDs, and unit IDs.
         * @returns {ApiResponse<UserRoles>} - The response containing the updated user information.
         */
        assignRole: build.mutation<
            ApiResponse<UserRoles>,
            RequestAddPermissionBody
        >({
            query: (body) => ({
                url: `users/${body.userId}/roles/${body.roleId}`,
                method: 'POST',
                body: {
                    organizationId: body.organizationId,
                    sportIds: body.sportIds,
                    unitIds: body.unitIds,
                },
            }),
            invalidatesTags: [
                ORGANIZATION_TAG,
                ROLES_TAG,
                UNIT_ROLES_AND_PERMISSIONS_TAG,
            ],
        }),

        /**
         * Edits an assigned role for a user.
         * @param {RequestEditPermissionBody} body - The body of the request containing user role ID and data to update.
         * @returns {ApiResponse<UserRoles>} - The response containing the updated user information.
         */
        editAssignedRole: build.mutation<
            ApiResponse<UserRoles>,
            RequestEditPermissionBody
        >({
            query: (body) => ({
                url: `user-roles/${body.userRoleId}`,
                method: 'PATCH',
                body: body.data,
            }),

            invalidatesTags: [
                ORGANIZATION_TAG,
                ROLES_TAG,
                UNIT_ROLES_AND_PERMISSIONS_TAG,
                INDIVIDUAL_USER,
            ],
        }),

        /**
         * Removes a user role by ID.
         * @param {string} ids - The IDs of the user roles to remove.
         * @returns {ApiResponse<UserRoles>} - The response containing the updated user information.
         */
        removeUserRole: build.mutation<ApiResponse<UserRoles>, string>({
            query: (ids) => ({
                url: `user-roles?ids=${ids}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                ORGANIZATION_TAG,
                ROLES_TAG,
                USERS_TAG,
                UNIT_ROLES_AND_PERMISSIONS_TAG,
            ],
        }),

        /**
         * Fetches users by their role.
         * @returns {DataWithMeta<UserRoles>} - The users associated with the role with pagination.
         */
        getUserByRole: build.query<DataWithMeta<UserRole>, Params>({
            query: (params) => ({
                url: `user-roles`,
                method: 'GET',
                params: params,
            }),
            providesTags: [ROLES_TAG],
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
    }),
})

export const {
    useGetUserRolesQuery,
    useGetRolesQuery,
    useAssignRoleMutation,
    useEditAssignedRoleMutation,
    useRemoveUserRoleMutation,
    useGetUserByRoleQuery,
} = rolesApi
